import {ICartPosition} from "@connox/typescript-library";
import {IAddressResponseData} from "@connox/typescript-library/types/checkout/AddressResponseData";
import * as React from "react";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import CartDetails from "../../../cart/CartDetails/CartDetails";
import "../../../lib/ArrowBorder.scss";
import {Button, ButtonStyles, ButtonType} from "../../../lib/form/Button/Button";
import "../../../lib/SimpleRadioList.scss";
import LineLoader from "../../../utils/loader/LineLoader";
import ListLoader from "../../../utils/loader/ListLoader";
import Spinner from "../../../utils/loader/Spinner";
import {CheckoutError, DEFAULT_ERROR_MESSAGE, IIsVisible} from "../../Checkout";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {useCheckoutViewContext} from "../../CheckoutContextProvider/CheckoutViewContextProvider";
import {isSameAddresses} from "../AddressStep/AddressStep";
import ShippingDatePicker from "./ShippingDatePicker";
import ShippingOptionRow from "./ShippingOptionRow";
import ShippingPartial from "./ShippingPartial";
import ShippingPhoneNo from "./ShippingPhoneNo";

const Shipping: React.FC<IIsVisible> = (props) => {
  const {checkout} = useCheckoutContext();
  const {shippingOptions} = useCheckoutViewContext();
  const {deliveryAddress, favouredDateShipping, invoiceAddress, isPartDeliveryAllowed, logisticsTypeId} = checkout;
  const {t} = useTranslation("checkout");
  const [logisticsType, setLogisticsType] = useState<number | null>(logisticsTypeId || null);
  const [isPartialAllowed, setIsPartialAllowed] = useState<boolean>(isPartDeliveryAllowed);
  const [shippingDate, setShippingDate] = useState<string | null>(favouredDateShipping);
  const buttonText = t("Weiter");
  const [{buttonValue, errors, isPhoneNumberValid, phoneNo}, setErrorState] = useState<{
    buttonValue: string | JSX.Element;
    errors: string[];
    isPhoneNumberValid: boolean;
    phoneNo?: string;
  }>({
    buttonValue: buttonText,
    errors: [],
    isPhoneNumberValid: true,
  });

  useEffect(() => {
    // when shippingOptions are retrieved, but errors are still set, reset them
    if (shippingOptions && errors.length) {
      setErrorState((prevState) => ({...prevState, errors: []}));
    } else if (!shippingOptions) {
      setErrorState((prevState) => ({...prevState, errors: [t("Die Versandarten konnten nicht geladen werden!")]}));
    }
  }, [shippingOptions]);

  useEffect(() => {
    setErrorState((prevState) => {
      const phoneErrorMessage = deliveryAddress?.messages?.find((msg) => msg.field === "phone");

      return {
        ...prevState,
        buttonValue: buttonText,
        errors: phoneErrorMessage ? [t(phoneErrorMessage.code, t(DEFAULT_ERROR_MESSAGE))] : [],
        isPhoneNumberValid: !phoneErrorMessage,
      };
    });
  }, [deliveryAddress?.messages]);

  useEffect(() => {
    if (props.isVisible) {
      setErrorState((prevState) => ({...prevState, phoneNo: deliveryAddress?.phone || undefined}));
    } else {
      setErrorState((prevState) => ({...prevState, buttonValue: buttonText, errors: []}));
    }
  }, [props.isVisible]);

  if (!props.isVisible) {
    return null;
  }

  if (!shippingOptions) {
    return (
      <div className="Shipping Mobile-Only-Friendly">
        <h1>{t("Versandoptionen")}</h1>
        <ListLoader />
        <LineLoader speed={4} linePositionY={"2"} linePositionX={"0"} lineHeight={"3"} lineWidth={"50"} />
      </div>
    );
  }

  if (!shippingOptions.length) {
    return (
      <div className="Shipping Mobile-Only-Friendly">
        <p>{t("Es konnten keine Versandoptionen geladen werden.")}</p>
      </div>
    );
  }

  const preferredShipping = shippingOptions.find((shippings) => !!shippings.isCustomerPreferredShipping);
  const bestMatch = shippingOptions.find((shipping) => shipping.isBestMatch);
  // retrieve user selected, or previously preferred, or best matching shipping id
  const checkedShippingId = logisticsType || preferredShipping?.logisticsTypeId || bestMatch?.logisticsTypeId;

  // PhoneNo for forwarder needed, and valid
  const isPhoneRequired = !!shippingOptions.find(
    (shippingOption) => checkedShippingId === shippingOption.logisticsTypeId && shippingOption.isPhoneRequired,
  );
  const phoneNumberNeededAndValid = isPhoneRequired && isPhoneNumberValid;
  const isButtonEnabled = !!checkedShippingId && (phoneNumberNeededAndValid || !isPhoneRequired);

  // post shipping parameters on continue
  const postShippingParameters = async (evt: React.MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();
    if (!isButtonEnabled) {
      return;
    }
    if (isPhoneRequired && !phoneNo) {
      setErrorState((prevState) => ({...prevState, isPhoneNumberValid: false}));

      return;
    }
    setErrorState((prevState) => ({...prevState, buttonValue: <Spinner />}));
    let shippingParams = {
      favouredDateShipping: shippingDate || undefined,
      isPartDeliveryAllowed: isPartialAllowed,
      logisticsTypeId: checkedShippingId,
    };
    if (deliveryAddress && phoneNo) {
      const addressWithPhoneNumber: IAddressResponseData = {...deliveryAddress, ...{phone: phoneNo}};
      if (isSameAddresses(invoiceAddress, deliveryAddress)) {
        shippingParams = {
          ...shippingParams,
          ...{address: {invoice: addressWithPhoneNumber, delivery: addressWithPhoneNumber}},
        };
      } else {
        shippingParams = {...shippingParams, ...{address: {delivery: addressWithPhoneNumber}}};
      }
    }

    try {
      await checkout.put(shippingParams);
    } catch (err) {
      setErrorState((prevState) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
        const result = err.originalError?.response?.data.result as {field: string; code: string} | undefined;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
        const errMessage = result?.code || err.originalError?.message || err.message;

        return {
          ...prevState,
          buttonValue: buttonText,
          errors: [t(errMessage, t(DEFAULT_ERROR_MESSAGE))],
          isPhoneNumberValid: result?.field !== "phone",
        };
      });
    }
  };

  const onPhoneNumberChange = (phoneNumber: string) => {
    setErrorState((prevState) => ({
      ...prevState,
      isPhoneNumberValid: phoneNumber.length > 0 ? true : prevState.isPhoneNumberValid,
      phoneNo: phoneNumber,
    }));
  };

  const showPartialDeliveryInput =
    checkout.cartData.positions.filter(
      (pos: ICartPosition) => pos.reference && pos.reference.itemId !== null && !!pos.item,
    ).length !== 1;

  return (
    <div className="Shipping Mobile-Only-Friendly">
      <h1>{t("Versandoptionen")}</h1>
      <CheckoutError>{errors}</CheckoutError>
      <form
        action="#"
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        {/* List of shipping options */}
        <div className="SimpleRadioList ArrowBorder">
          {shippingOptions.map((option) => {
            const isSelected = option.logisticsTypeId === checkedShippingId;

            return (
              <ShippingOptionRow
                key={`shippingoption${option.logisticsTypeId}`}
                option={option}
                onShippingChanged={() => setLogisticsType(option.logisticsTypeId)}
                selected={isSelected}
              />
            );
          })}
        </div>
        {/* Phone no. input if forwarder present */}
        {isPhoneRequired && <ShippingPhoneNo {...{isPhoneNumberValid, onPhoneNumberChange, phoneNo}} />}
        {/* Allow partial delivery input */}
        {showPartialDeliveryInput && (
          <ShippingPartial isAllowed={isPartialAllowed} onPartialDelivery={setIsPartialAllowed} />
        )}
        {/* Desired shipping date input */}
        <ShippingDatePicker deliveryDate={shippingDate} onDeliveryDateChange={setShippingDate} />
        <CartDetails reduced />
        <Button
          action={postShippingParameters}
          styles={[ButtonStyles.wide]}
          type={isButtonEnabled && typeof buttonValue === "string" ? ButtonType.default : ButtonType.disabled}
        >
          {buttonValue}
        </Button>
      </form>
    </div>
  );
};

export default React.memo(Shipping);
